// ============================================================================
// Hooks TanStack Query — ACCUEIL : lectures du tableau de bord social (E1a)
// + fil de commentaires (E1b — les ecritures vivent dans useAccueilMutations)
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (visibilite etage 3 des publications, 403 autoritaire sur indicateurs/
// comites/equipes — D4 ; myReaction et canDelete des commentaires = verdicts
// SERVEUR, jamais calcules client — bind-to-fetch) ; le SPA gate les 3
// requetes management AVANT fetch sur viewer.canViewManagement du fil
// (FIX-403 D2 — zero requete refusee emise, anti « delegated 403 stream »
// Sev1 juin) et ne MONTE les cartes que sur ce meme booleen (D3, controles
// capability-aware §4c). Un 503 MIGRATION_144_PENDING (tables gest.* absentes)
// = bandeau explicite, pas un etat d'erreur retryable.

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';

// ─── Types miroir des DTOs serveur (server/repositories/dashboard.ts) ────────

export type PublicationCategory = 'general' | 'contrats' | 'rh' | 'evenements' | 'securite' | 'operations';
export const PUBLICATION_CATEGORIES: PublicationCategory[] = [
  'general', 'contrats', 'rh', 'evenements', 'securite', 'operations',
];

export type ReactionType = 'like' | 'love' | 'celebrate';

export interface Publication {
  id: string;
  title: string;
  body: string;
  category: PublicationCategory;
  authorEmail: string;
  authorName: string | null;
  postedAt: string;
  /**
   * La publication porte-t-elle une image ? BOOLEEN (CHORE-GEST-BLOBS) — l'API ne
   * renvoie JAMAIS le chemin du blob. Le client derive l'URL d'app
   * `/api/publications/<id>/image` (proxy serveur sous Bearer) et la materialise
   * en object URL : un `<img src>` nu n'emporte pas le token.
   */
  hasImage: boolean;
  reactions: { like: number; love: number; celebrate: number };
  viewCount: number;
  commentCount: number;
  /** LA reaction active du viewer — verdict SERVEUR (E1b), jamais calcule client. */
  myReaction: ReactionType | null;
}

export interface PublicationComment {
  id: string;
  authorEmail: string;
  authorName: string | null;
  body: string;
  createdAt: string;
  /** Affordance suppression (auteur session | admin) — verdict SERVEUR (bind-to-fetch). */
  canDelete: boolean;
}

export interface PublicationCommentsResponse {
  comments: PublicationComment[];
}

export interface PublicationsPage {
  publications: Publication[];
  nextCursor: string | null;
  viewer: {
    role: 'admin' | 'manager' | 'director' | 'employee';
    canPublish: boolean;
    /** Gating capability-aware AVANT fetch (FIX-403 D2) : les 3 requêtes management
     *  (indicateurs/comités/santé) ne partent QUE si true — zéro requête refusée émise. */
    canViewManagement: boolean;
  };
}

export interface IndicatorComparison {
  value: number;
  vsBudget: number;
  vsPriorYear: number;
}

export interface IndicatorsSummary {
  month: string | null;
  revenue: IndicatorComparison | null;
  netProfit: IndicatorComparison | null;
  pulses: { answered: number; total: number; mood: number | null; period: string };
  postings: { count: number };
}

export interface CommitteeActivity {
  id: string;
  name: string;
  milestonesDone: number;
  milestonesTotal: number;
  lastActivityAt: string;
}

export interface CommitteesActivity {
  committees: CommitteeActivity[];
  total: number;
}

export interface TeamHealth {
  id: string;
  name: string;
  directorName: string | null;
  moodScore: number | null;
}

export interface TeamsHealth {
  period: string;
  answered: number;
  total: number;
  mood: number | null;
  teams: TeamHealth[];
}

// ─── Etats capability (§4c) — le 403 n'est PLUS un etat client : les requetes
// management sont gatees AVANT fetch sur viewer.canViewManagement (FIX-403 D2/D3,
// anti « delegated 403 stream ») ; seul le 503 migration reste a detecter. ──────

export function isMigrationPending(err: unknown): boolean {
  return err instanceof ApiServerError && err.status === 503;
}

/**
 * Code court d'erreur pour les actions du fil (E1b) : mappe sur
 * gest:accueil.errors.* — jamais de code brut a l'ecran (lecon GA-21).
 */
export function accueilErrorCode(err: unknown): string {
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

const KEY = ['gest', 'accueil'] as const;

/** Cles partagees avec useAccueilMutations (mises a jour optimistes ciblees). */
export const ACCUEIL_FEED_KEY = [...KEY, 'publications'] as const;
export const accueilCommentsKey = (publicationId: string) =>
  [...KEY, 'comments', publicationId] as const;

/** Fil pagine keyset — pages concatenees par useInfiniteQuery (E1a lecture). */
export function usePublicationsFeed(category: PublicationCategory | null, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...ACCUEIL_FEED_KEY, { category }],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (pageParam) params.set('cursor', pageParam);
      const qs = params.toString();
      return api.get<PublicationsPage>(`/api/publications${qs ? `?${qs}` : ''}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
}

/**
 * Fil de discussion d'une publication (E1b) — fetch au depliage seulement
 * (enabled = zone ouverte). 404 = publication illisible pour ce viewer
 * (bind-to-fetch serveur) : etat stable, zero retry.
 */
export function usePublicationComments(publicationId: string, enabled: boolean) {
  return useQuery({
    queryKey: accueilCommentsKey(publicationId),
    queryFn: () => api.get<PublicationCommentsResponse>(`/api/publications/${publicationId}/comments`),
    enabled,
    retry: false,
  });
}

export function useIndicatorsSummary(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'indicators'],
    queryFn: () => api.get<IndicatorsSummary>('/api/indicators/summary'),
    enabled,
    // 503 (migration pendante) est un etat STABLE — zero retry. (Le 403 n'est
    // plus atteignable en usage normal : enabled = canViewManagement, D2.)
    retry: false,
  });
}

export function useCommitteesActivity(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'committees'],
    queryFn: () => api.get<CommitteesActivity>('/api/committees/activity'),
    enabled,
    retry: false,
  });
}

export function useTeamsHealth(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'teams'],
    queryFn: () => api.get<TeamsHealth>('/api/teams/health'),
    enabled,
    retry: false,
  });
}
